/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { Branding } from "../../model/branding";
import { DeviceModel } from "../../model/device";
import { BrandingUiInfoKeys } from "../../model/entities/BrandingUiInfoKeys";
import { Log } from "../../model/logging";
import { selectIsBrandingFetched } from "../appStartup";
import type { UnityRootState } from "../UnityAppStore";
import type { MobileLandingDialogState } from "./mobileLandingDialog.types";

const TAG: string = "mobileLandingDialogSlice";

/**
 * Routes on which the mobile landing dialog must NOT appear.
 * Any path containing "password" (forgot-password, password.html, etc.)
 * or the request-new-link route is suppressed so those flows are uninterrupted.
 */
// todo: fix hard coded strings
const SUPPRESSED_ROUTE_PATTERNS: ReadonlyArray<string> = [
  "password",
  "/request-new-link"
];

/**
 * Returns true when the dialog should be suppressed for the given pathname.
 */
function isSuppressedRoute(pathname: string): boolean {
  return SUPPRESSED_ROUTE_PATTERNS.some((pattern) =>
    pathname.includes(pattern)
  );
}

const INITIAL_STATE: MobileLandingDialogState = {
  iosAppStoreUrl: null,
  androidPlayStoreUrl: null,
  appScheme: null
};

/**
 * Resolves and stores the platform-specific app store URLs from the cached
 * branding bundle. Should be dispatched when the dialog becomes visible.
 */
export const resolveAppStoreUrls = createAsyncThunk<
  {
    iosAppStoreUrl: string | null;
    androidPlayStoreUrl: string | null;
    appScheme: string | null;
  },
  void,
  { rejectValue: string }
>(
  "mobileLandingDialog/resolveAppStoreUrls",
  async (_: void, { getState, rejectWithValue }) => {
    try {
      const state = getState() as UnityRootState;
      if (!selectIsBrandingFetched(state)) {
        Log.d(TAG, "resolveAppStoreUrls() — skipped, branding not yet fetched");
        return rejectWithValue("Branding data not yet available");
      }

      Log.d(TAG, "resolveAppStoreUrls() called");

      const iosAppId: unknown = Branding.getBrandingUiInfoForKey(
        BrandingUiInfoKeys.IOS_APP_ID
      );
      const androidAppId: unknown = Branding.getBrandingUiInfoForKey(
        BrandingUiInfoKeys.ANDROID_APP_ID
      );
      const rawAppScheme: unknown = Branding.getBrandingUiInfoForKey(
        BrandingUiInfoKeys.APP_SCHEME
      );

      const iosAppStoreUrl: string | null =
        typeof iosAppId === "string" && iosAppId !== ""
          ? `https://apps.apple.com/app/${iosAppId}`
          : null;

      const androidPlayStoreUrl: string | null =
        typeof androidAppId === "string" && androidAppId !== ""
          ? `https://play.google.com/store/apps/details?id=${androidAppId}`
          : null;

      const appScheme: string | null =
        typeof rawAppScheme === "string" && rawAppScheme !== ""
          ? rawAppScheme
          : null;

      Log.d(TAG, "resolveAppStoreUrls() — complete");
      return { iosAppStoreUrl, androidPlayStoreUrl, appScheme };
    } catch (error: unknown) {
      Log.e(TAG, "resolveAppStoreUrls()", error);
      const message: string =
        error instanceof Error
          ? error.message
          : "Failed to resolve app store URLs";
      return rejectWithValue(message);
    }
  }
);

/**
 * Attempts to open the installed native app via its deep-link scheme.
 * Falls back to the provided app store URL after 1.5 seconds if the app
 * does not open (i.e., is not installed).
 *
 * @param appStoreUrl - The platform-specific app store URL to fall back to.
 */
export const openNativeApp = createAsyncThunk<
  void,
  string,
  { rejectValue: string }
>(
  "mobileLandingDialog/openNativeApp",
  async (appStoreUrl: string, { getState, rejectWithValue }) => {
    try {
      Log.d(TAG, "openNativeApp() called");

      const state = getState() as UnityRootState;
      const appScheme: string | null = state.mobileLandingDialog.appScheme;

      if (appScheme === null || appScheme === "") {
        Log.w(
          TAG,
          "openNativeApp() — no app scheme available, falling back to store"
        );
        window.location.href = appStoreUrl;
        return;
      }

      // Fallback to app store if the app doesn't open within 1.5 seconds
      const fallbackTimer = setTimeout(() => {
        window.location.href = appStoreUrl;
      }, 1500);

      // If the app opens, the page becomes hidden — clear the fallback timer
      const handleVisibilityChange = (): void => {
        if (document.hidden) {
          clearTimeout(fallbackTimer);
          document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange
          );
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      // Attempt to open the installed app via deep-link scheme
      window.location.href = appScheme + "://";
    } catch (error: unknown) {
      Log.e(TAG, "openNativeApp()", error);
      const message: string =
        error instanceof Error ? error.message : "Failed to open native app";
      return rejectWithValue(message);
    }
  }
);

const mobileLandingDialogSlice = createSlice({
  name: "mobileLandingDialog",
  initialState: INITIAL_STATE,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(resolveAppStoreUrls.fulfilled, (state, action) => {
      state.iosAppStoreUrl = action.payload.iosAppStoreUrl;
      state.androidPlayStoreUrl = action.payload.androidPlayStoreUrl;
      state.appScheme = action.payload.appScheme;
    });
    builder.addCase(resolveAppStoreUrls.rejected, (state) => {
      state.iosAppStoreUrl = null;
      state.androidPlayStoreUrl = null;
      state.appScheme = null;
    });
  }
});

export default mobileLandingDialogSlice.reducer;

/**
 * Selects whether the mobile landing dialog should be visible for the given route.
 * The dialog appears on all mobile-browser routes except password-related flows.
 *
 * @param pathname - The current location pathname from React Router.
 */
export const selectIsDialogVisible = (
  _state: UnityRootState,
  pathname: string
): boolean => DeviceModel.isMobileDevice() && !isSuppressedRoute(pathname);

/**
 * Selects the app store URL for the current device platform.
 * Returns the iOS App Store URL on iOS devices, Play Store URL on Android.
 */
export const selectCurrentPlatformAppStoreUrl = (
  state: UnityRootState
): string | null => {
  const isIOSDevice: boolean =
    DeviceModel.isChromeIOS() ||
    DeviceModel.isFirefoxIOS() ||
    /iPad|iPhone|iPod/i.test(navigator.userAgent);

  if (isIOSDevice) {
    return state.mobileLandingDialog.iosAppStoreUrl;
  }
  return state.mobileLandingDialog.androidPlayStoreUrl;
};
